import {
  DownOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Empty, Flex, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { uploadContract } from "../../../Contracts/redux/contracts.thunks.ts"
import { TEMPLATE_DOCUMENT_TYPES } from "../../../DocumentTemplates/constants/template-document-type.tsx"
import { uploadProposal } from "../../../Proposals/redux/proposals.thunks.ts"
import { LeadViewContracts } from "../../LeadViewPage/components/LeadViewContract/LeadViewContracts.tsx"
import { LeadViewContractsCreateTrigger } from "../../LeadViewPage/components/LeadViewContract/LeadViewContractsCreateTrigger.tsx"
import { LeadViewDocumentUpload } from "../../LeadViewPage/components/LeadViewDocument/LeadViewDocumentUpload.tsx"
import { LeadViewProposals } from "../../LeadViewPage/components/LeadViewProposal/LeadViewProposals.tsx"
import { LeadViewTemplatesModalTrigger } from "../../LeadViewPage/components/LeadViewTemplatesModal/LeadViewTemplatesModalTrigger.tsx"
import type {
  LeadContract,
  LeadProposal
} from "../../types/lead.documents.types.ts"
import type { Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { DocumentCreateMenuOption } from "./DocumentCreateMenuOption.tsx"
import { leadDocumentVisibility } from "./lead-documents-visibility.ts"
import { OverviewCardIcon } from "../overview/OverviewCardIcon.tsx"

const { Text } = Typography

interface LeadDocumentsTabProps {
  lead: Lead
  canEdit?: boolean
  onCreateContractFromProposal: (proposalId: string) => Promise<void>
}

/** Icon + title + subtitle, built as one node since `WorkspaceCard.title` has no separate subtitle slot. */
function DocumentSectionTitle({
  icon,
  accent,
  title,
  description
}: {
  icon: React.ReactNode
  accent: "indigo" | "green"
  title: string
  description: string
}) {
  return (
    <Flex align={"center"} gap={"middle"}>
      <OverviewCardIcon icon={icon} accent={accent} size={40} />
      <Flex vertical gap={0}>
        <span>{title}</span>
        <Text type={"secondary"} className={"text-xs font-normal"}>
          {description}
        </Text>
      </Flex>
    </Flex>
  )
}

/**
 * Relocated from the Overview grid into its own tab — the mockup's cards had
 * no slot for a document workflow this substantial, and one card per section
 * (rather than the pair of `<section>`s inside a single card) matches how
 * every other tab in this workspace composes itself (see `LeadScopeTab`).
 * The stage gating, the three contract-creation paths (from proposal / from
 * template / upload) and every other behavior are unchanged from
 * `LeadOverviewDocuments` — only where and how this renders moved.
 */
function LeadDocumentsTab({
  lead,
  canEdit,
  onCreateContractFromProposal
}: LeadDocumentsTabProps) {
  const { t } = useTranslation()
  const leadId = lead?.id
  const [proposalMenuOpen, setProposalMenuOpen] = useState(false)

  const {
    isProposalStage,
    canCreateProposal,
    showProposals,
    canCreateContract,
    canUploadContract,
    showContracts
  } = leadDocumentVisibility(lead)

  return (
    <Flex vertical gap={"middle"} data-testid={"lead-documents-tab"}>
      {showProposals && (
        <WorkspaceCard
          cardId={"documents-proposals"}
          title={
            <DocumentSectionTitle
              icon={<FileTextOutlined />}
              accent={"indigo"}
              title={t("leadWorkspace.cards.documents.proposals")}
              description={t(
                "leadWorkspace.cards.documents.proposalsDescription"
              )}
            />
          }
          ariaTitle={t("leadWorkspace.cards.documents.proposals")}
          extra={
            canEdit &&
            canCreateProposal && (
              <Flex gap={"small"}>
                <LeadViewDocumentUpload<LeadProposal>
                  leadId={leadId}
                  documentType={"proposal"}
                  outlined
                  label={t("leadWorkspace.cards.documents.uploadProposal")}
                  uploadThunk={uploadProposal}
                />
                <Dropdown
                  trigger={["click"]}
                  open={proposalMenuOpen}
                  onOpenChange={setProposalMenuOpen}
                  popupRender={() => (
                    <div
                      className={
                        "w-80 rounded-card border border-border bg-white p-2 shadow-lg"
                      }
                    >
                      <Text
                        type={"secondary"}
                        className={"text-xs px-2 block mb-1"}
                      >
                        {t(
                          "leadWorkspace.cards.documents.createProposalMenuTitle"
                        )}
                      </Text>
                      <LeadViewTemplatesModalTrigger
                        type={TEMPLATE_DOCUMENT_TYPES.PROPOSAL}
                        leadId={leadId}
                        render={(onClick) => (
                          <DocumentCreateMenuOption
                            icon={<FileTextOutlined />}
                            accent={"indigo"}
                            title={t(
                              "leadWorkspace.cards.documents.fromProposalTemplate"
                            )}
                            description={t(
                              "leadWorkspace.cards.documents.fromProposalTemplateDescription"
                            )}
                            onClick={() => {
                              setProposalMenuOpen(false)
                              onClick()
                            }}
                          />
                        )}
                      />
                    </div>
                  )}
                >
                  <Button type={"primary"} icon={<PlusOutlined />}>
                    {t("leadWorkspace.cards.documents.newProposal")}
                    <DownOutlined className={"text-xs"} />
                  </Button>
                </Dropdown>
              </Flex>
            )
          }
        >
          {lead?.proposals?.length ? (
            <LeadViewProposals
              isUpdateAllowed={isProposalStage}
              viewOnly={!canEdit}
              proposals={lead?.proposals}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("leadWorkspace.cards.documents.emptyProposals")}
            />
          )}
        </WorkspaceCard>
      )}

      {showContracts && (
        <WorkspaceCard
          cardId={"documents-contracts"}
          title={
            <DocumentSectionTitle
              icon={<FileDoneOutlined />}
              accent={"green"}
              title={t("leadWorkspace.cards.documents.contracts")}
              description={t(
                "leadWorkspace.cards.documents.contractsDescription"
              )}
            />
          }
          ariaTitle={t("leadWorkspace.cards.documents.contracts")}
          extra={
            <Flex gap={"small"}>
              {canEdit && canUploadContract && (
                <LeadViewDocumentUpload<LeadContract>
                  leadId={leadId}
                  documentType={"contract"}
                  outlined
                  label={t("leadWorkspace.cards.documents.uploadContractButton")}
                  uploadThunk={uploadContract}
                />
              )}

              {canEdit && canCreateContract && (
                <LeadViewContractsCreateTrigger
                  leadId={leadId}
                  proposals={lead?.proposals}
                  onCreateFromProposal={onCreateContractFromProposal}
                />
              )}
            </Flex>
          }
        >
          {lead?.contracts?.length ? (
            <LeadViewContracts
              isUpdateAllowed={canCreateContract}
              viewOnly={!canEdit}
              contracts={lead?.contracts}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("leadWorkspace.cards.documents.emptyContracts")}
            />
          )}
        </WorkspaceCard>
      )}
    </Flex>
  )
}

export { LeadDocumentsTab }
